// Runs an async loader on mount (and whenever `deps` change) and exposes
// { data, loading, refreshing, error, reload }. The loader is read through a ref
// so `run` stays stable and re-runs are driven solely by `deps`.
//
// Two distinct pending states, because they mean different things on screen:
//   loading    — nothing to show yet (first load, or reloading after an error).
//                Screens blank to a loader.
//   refreshing — a reload with data already on screen. Screens KEEP that data up;
//                the heavier ones dim it via the `refreshing` class. Blanking here
//                is what made every save flash: it unmounted the whole subtree and
//                took local state (expanded lead cards, folds) down with it.
//
// Out-of-order guard: every run takes a sequence number and only the newest one may
// write state. Two quick saves fire two reloads, and without this the slower/earlier
// response can land last and leave stale data on screen with nothing to explain it.
//
// BACK TO THE TAB, BACK TO THE SERVER. Every screen fetches when it opens, but a screen
// left open in another tab never opens again: an expense saved in one tab left the
// engagement page and the money screen in the other showing the old figures until a
// manual refresh. So whatever is on screen re-reads quietly when its tab becomes visible
// again — the same reload a save triggers, dimmed, never blanked. A refetch nobody asked
// for that FAILS (a laptop waking before its network) keeps the data it had rather than
// replacing the screen with an error; an asked-for reload still reports its error.
import { useCallback, useEffect, useRef, useState } from 'react';

export default function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, pending: true, error: null });
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const seq = useRef(0);

  const run = useCallback((quiet = false) => {
    const id = ++seq.current;
    const isCurrent = () => id === seq.current;
    setState((s) => ({ ...s, pending: true }));
    return loaderRef.current()
      .then((data) => { if (isCurrent()) setState({ data, pending: false, error: null }); })
      .catch((error) => {
        if (!isCurrent()) return;
        setState((s) => (quiet && s.data != null
          ? { ...s, pending: false }
          : { data: null, pending: false, error }));
      });
  }, []);

  useEffect(() => {
    run();
  }, [run, ...deps]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') run(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [run]);

  // Never handed `run` itself: a screen passing reload as an onClick would pass the event
  // as `quiet` and turn its own failed reload into a silent one.
  const reload = useCallback(() => run(), [run]);

  return {
    data: state.data,
    error: state.error,
    loading: state.pending && state.data == null,
    refreshing: state.pending && state.data != null,
    reload,
  };
}
